import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import InfoIcon from '@mui/icons-material/Info';
import {
  Fade,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import { FormikProps } from 'formik';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';

import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import {
  trackClickedPowerListDialAtATimeDropdown,
  trackClickedPowerListMaxAttemptsButton,
  trackClickedPowerListMinDurationDaysField,
  trackClickedPowerListMinDurationHoursField,
  trackClickedPowerListMinDurationMinutesField,
  trackClickedPowerListNextPowerListDropdown,
  trackClickedPowerListSecondsBeforeTimeoutDropdown,
} from '../../../common/userTracking';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { priorityRange } from '../priorities';

const useStyles = makeStyles((theme: Theme) => ({
  webhook: {
    paddingLeft: '12px',
    width: '98.4%',
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '18vw',
  },
  field2: {
    marginTop: theme.spacing(1.5), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  field3: {
    marginTop: theme.spacing(2.5), // space between fields
  },
  tooltipField: {
    maxWidth: '90%',
  },
  smsField: {
    minHeight: '3em',
  },
  smsWidth: {
    width: '495px',
  },
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '110px',
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
  },
  timeframes2: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '75px',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  groupedInput: {
    marginTop: theme.spacing(2), // space between fields
    marginBottom: theme.spacing(1.75), // space between fields
  },
  infiniteField: {
    marginLeft: '-8px',
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      bottom: '-1.2rem',
    },
  },
}));

export const InfoTooltip = (props: Omit<TooltipProps, 'children'>) => {
  return (
    <Tooltip
      style={{ cursor: 'pointer' }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      placement="right-start"
      {...props}
    >
      <InfoIcon />
    </Tooltip>
  );
};

export const dayNames = [
  'placeholder',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const cellsReset = Array.from({ length: 7 + 1 }, (i) =>
  Array.from({ length: 48 + 1 }, (j) => false)
);

export const tableLookup = Array.from({ length: 7 + 1 }, (i, dayIndex) =>
  Array.from({ length: 48 + 1 }, (j, timeIndex) => ({
    day: dayNames[dayIndex],
    time: {
      startTime: moment()
        .startOf('day')
        .add((timeIndex - 1) * 30, 'minutes')
        .format('HH:mm'),

      endTime: moment()
        .startOf('day')
        .add(timeIndex * 30 - (timeIndex === 48 ? 1 : 0), 'minutes') // last time will be 11:59 pm
        .format('HH:mm'),
    },
  }))
);

const InfiniteInput = (props: {
  isInfinite: boolean;
  setIsInfinite: (val: boolean) => void;
  formik: any;
  label: string;
  name: string;
  width: string;
}) => {
  const { isInfinite, setIsInfinite, formik, label, name, width } = props;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div>
        {isInfinite ? (
          <TextField
            variant="outlined"
            size="small"
            style={{ margin: '8px', width: width }}
            name={name}
            label={label}
            onFocus={(e) => {
              if (isInfinite) {
                setIsInfinite(false);
                formik?.values?.[name] === 65535 &&
                  formik?.setFieldValue(name, 1);
              }
            }}
            InputProps={{
              inputProps: {
                min: 0,
                max: 90,
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      backgroundColor: '#0098C7',
                    }}
                    onClick={() => {
                      props?.formik?.setFieldValue(
                        name,
                        name === 'maxAttempts' ? 10 : 2
                      );
                      setIsInfinite(false);
                    }}
                    edge="end"
                    size="small"
                  >
                    <div style={{ color: 'white', display: 'contents' }}>
                      <AllInclusiveIcon fontSize="small" color="inherit" />
                    </div>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <FormikTextField
            style={{ margin: '8px', width: width }}
            formik={formik}
            name={name}
            label={label}
            size="small"
            InputProps={{
              inputProps: {
                min: 0,
                max: 90,
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      backgroundColor: 'inherit',
                      display: 'contents',
                    }}
                    onClick={() => {
                      trackClickedPowerListMaxAttemptsButton();
                      props?.formik?.setFieldValue(name, 65535);
                      setIsInfinite(true);
                    }}
                    edge="end"
                    size="small"
                  >
                    <AllInclusiveIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            type="number"
            variant="outlined"
          />
        )}
      </div>
    </div>
  );
};

export function Settings(props: {
  formik: FormikProps<any>;
  id: number | null;
  isAgentPowerlist: number | undefined;
  isMappingCRM: boolean;
}) {
  const classes = useStyles();
  const businessQueues = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  const queuesCached = useRef<any[]>([]);
  queuesCached.current = useMemo(
    () =>
      businessQueues.contents?.data
        ? [...(businessQueues.contents?.data?.result ?? [])].sort((a, b) =>
            a?.queuename
              ?.toLowerCase()
              ?.localeCompare(b?.queuename?.toLowerCase())
          )
        : queuesCached.current,
    [businessQueues]
  );
  const [isMaxAttemptsInfinite, setIsMaxAttemptsInfinite] = useState(false);
  const [isDailyAttemptsInfinite, setIsDailyAttemptsInfinite] = useState(false);

  const secondsBeforeTimeoutRange = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
  const dialAtATimeRange = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  React.useEffect(() => {
    setIsMaxAttemptsInfinite(props?.formik?.values?.maxAttempts === 65535);
    setIsDailyAttemptsInfinite(
      props?.formik?.values?.maxAttemptsPerDay === 65535
    );
    // setIsMaxDaysInfinite(props?.formik?.values?.maxDays === 0);
  }, [props?.formik]);
  //bool to determine if this is an agent powerlist
  const isAgentPowerlist =
    props.isAgentPowerlist === 1 ||
    (props.id === undefined &&
      (window.location.pathname.includes('manage/agents') ||
        window.location.pathname.includes('my-profile')));

  const powerlistReportLoadable = useRecoilValueLoadable(
    isAgentPowerlist //if agent powerlist
      ? //get the agent assigned powerlists
        makeAPIRequest({
          method: 'get',
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: `${endpointEnum.GET_USER_TEAM_POWERLISTS}/${
            window.location.pathname.match(
              /[/manage/agents/|/my\-profile/](\d{1,})[/powerlists/]/ //pull ID from URL
            )?.[1]
          }`,
        })
      : //else get all business powerlists
        makeAPIRequest({
          endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
          method: 'POST',
          payloadJsonString: JSON.stringify({
            call: 'v1/getBusinessPowerlists',
          }),
        })
  );

  const powerlists = useMemo(
    () =>
      isAgentPowerlist
        ? //if this is an agent powerlist, process the agent powerlist payload
          (powerlistReportLoadable?.contents?.response as AxiosResponse)
            ?.data ??
          powerlistReportLoadable?.contents?.respowerlists ??
          []
        : //else parse business powerlist payload
          (powerlistReportLoadable?.contents?.response as AxiosResponse)?.data
            ?.powerlists ??
          powerlistReportLoadable?.contents?.respowerlists ??
          [],
    [powerlistReportLoadable]
  );

  const { isMappingCRM } = props;

  return (
    <>
      <div className="container">
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">PowerList Name</h5>
            <p className="fieldDescription">
              Enter a name for your PowerList. This will show up on the Kixie
              Dashboard and in PowerDialer for agents subscribed to the list.
              Choose something unique to help agents understand the objective
              and numbers associated with the list.
            </p>
          </div>
          <div className="col-lg-4">
            {isMappingCRM ? (
              <p>{props.formik.values.name}</p>
            ) : (
              <FormikTextField
                dataPup="powerlist-name-field"
                formik={props.formik}
                fullWidth
                name="name"
                inputProps={{ maxLength: 300 }}
                label="PowerList Name"
                required
                size="small"
                variant="outlined"
                className={clsx(classes.field2, classes.tooltipField)}
              />
            )}
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Priority</h5>
            <p className="fieldDescription">
              Phone numbers in a PowerList with a higher priority will be dialed
              first. PowerLists with the same priority level will be dialed
              together.
            </p>
          </div>
          <div className="col-lg-4">
            <FormikSelect
              name="powerlistPriority"
              className={clsx(classes.field2, classes.tooltipField)}
              formik={props.formik}
              label="Priority"
              size="small"
              variant="outlined"
              fullWidth
            >
              {priorityRange.map((selection, index) => {
                return (
                  <MenuItem value={selection?.value} key={index}>
                    {selection?.name}
                  </MenuItem>
                );
              })}
            </FormikSelect>
          </div>
        </div>
        {!isMappingCRM && (
          <>
            <div className="row borderBottom">
              <div className="col-lg-8">
                <h5 className="fieldTitle">Max Attempts</h5>
                <p className="fieldDescription">
                  How many times a phone number can be dialed in a single day
                  and how many times a phone number is dialed in total before
                  being removed or moving to a new PowerList.
                </p>
              </div>
              <div className="col-lg-4">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                  }}
                  className={classes.infiniteField}
                >
                  <InfiniteInput
                    isInfinite={isDailyAttemptsInfinite}
                    setIsInfinite={setIsDailyAttemptsInfinite}
                    formik={props?.formik}
                    label={'Daily'}
                    name={`maxAttemptsPerDay`}
                    width="100px"
                  />

                  <InfiniteInput
                    isInfinite={isMaxAttemptsInfinite}
                    setIsInfinite={setIsMaxAttemptsInfinite}
                    formik={props?.formik}
                    label={'Total'}
                    name={`maxAttempts`}
                    width="100px"
                  />
                </div>
              </div>
            </div>
            <div className="row borderBottom">
              <div className="col-lg-8">
                <h5 className="fieldTitle">Min Duration Before Call Retry</h5>
                <p className="fieldDescription">
                  The minimum duration between dial attempts for a phone number.
                  The actual duration between dial attempts could be longer than
                  the minimum duration.
                </p>
              </div>
              <div className="col-lg-4">
                <FormikTextField
                  className={clsx(classes.timeframes2, classes.groupedInput)}
                  formik={props.formik}
                  name={`minDurationDays`}
                  label="Days"
                  size="small"
                  style={{ marginLeft: 0 }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 90,
                    },
                  }}
                  type="number"
                  variant="outlined"
                  onClick={() => {
                    trackClickedPowerListMinDurationDaysField();
                  }}
                />
                <FormikTextField
                  className={clsx(classes.timeframes2, classes.groupedInput)}
                  formik={props.formik}
                  name={`minDurationHours`}
                  label="Hours"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 23,
                    },
                  }}
                  size="small"
                  type="number"
                  variant="outlined"
                  onClick={() => {
                    trackClickedPowerListMinDurationHoursField();
                  }}
                />
                <FormikTextField
                  className={clsx(classes.timeframes2, classes.groupedInput)}
                  formik={props.formik}
                  name={`minDurationMinutes`}
                  label="Minutes"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 59,
                    },
                  }}
                  size="small"
                  type="number"
                  variant="outlined"
                  onClick={() => {
                    trackClickedPowerListMinDurationMinutesField();
                  }}
                />
              </div>
            </div>
          </>
        )}
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Seconds before Timeout</h5>
            <p className="fieldDescription">
              When dialing a phone number, how long should we wait for an
              answer-event before automatically disconnecting?
            </p>
          </div>
          <div className="col-lg-4">
            <FormikSelect
              name="secondsBeforeTimeout"
              className={clsx(classes.field2, classes.tooltipField)}
              formik={props.formik}
              label="Seconds before Timeout"
              size="small"
              variant="outlined"
              fullWidth
              style={{ marginBottom: 5 }}
              onClick={() => {
                trackClickedPowerListSecondsBeforeTimeoutDropdown();
              }}
            >
              {secondsBeforeTimeoutRange.map((selection, index) => {
                return (
                  <MenuItem value={selection} key={index}>
                    {selection}
                  </MenuItem>
                );
              })}
            </FormikSelect>
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Dial at a Time</h5>
            <p className="fieldDescription">
              Determines how many phone numbers are dialed simultaneously for
              each agent in the PowerList. If you select below 2 and AI Human
              Detect is enabled, Kixie will enforce a minimum of 2 numbers to
              dial at a time regardless of what is selected in order to function
              correctly. We recommend a minimum of 4 numbers to dial at a time
              if AI Human Detect is enabled.
            </p>
          </div>
          <div className="col-lg-4">
            <FormikSelect
              name="dialAtATime"
              className={clsx(classes.field2, classes.tooltipField)}
              formik={props.formik}
              label="Dial at a Time"
              size="small"
              variant="outlined"
              fullWidth
              data-pup="dial-at-a-time"
              style={{ marginBottom: 10 }}
              onClick={() => {
                trackClickedPowerListDialAtATimeDropdown();
              }}
            >
              {dialAtATimeRange.map((selection, index) => {
                return (
                  <MenuItem
                    value={selection}
                    key={index}
                    data-pup={`dial-at-a-time-${selection}`}
                  >
                    {selection}
                  </MenuItem>
                );
              })}
            </FormikSelect>
          </div>
        </div>
        {!isMappingCRM && (
          <div className="row">
            <div className="col-lg-8">
              <h5 className="fieldTitle">Next PowerList</h5>
              <p className="fieldDescription">
                When a phone number reaches its Total Max dial attempts without
                being removed from this PowerList, it can be forwarded to
                another PowerList in your account.
              </p>
            </div>
            <div className="col-lg-4">
              <MaterialAutocomplete
                type="text"
                name="nextPowerlistId"
                className={clsx(
                  classes.field2,
                  classes.field3,
                  classes.tooltipField
                )}
                fullWidth
                label="Next PowerList"
                size="small"
                variant="outlined"
                disabled={props.formik.values?.maxAttempts === 65535}
                options={[
                  { name: 'None', powerlistId: 0 },
                  ...(powerlists ?? []),
                ]
                  ?.filter(
                    (powerlist) =>
                      (isAgentPowerlist
                        ? powerlist.isAgentPowerlist === 1 ||
                          powerlist.powerlistId === 0
                        : powerlist.isAgentPowerlist !== 1) &&
                      (Number(props?.id) === 0
                        ? true
                        : powerlist.nextPowerlistId !== Number(props?.id)) &&
                      powerlist.powerlistId !== Number(props?.id)
                  )
                  ?.sort((a, b) => {
                    if (a.powerlistId === 0) return -1;
                    if (b.powerlistId === 0) return 1;
                    return a?.name
                      ?.toLowerCase()
                      ?.localeCompare(b?.name?.toLowerCase());
                  })}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) =>
                  option.powerlistId === value?.powerlistId
                }
                // Ensure that value is always controlled and not undefined
                value={
                  props?.formik?.values?.nextPowerlistId !== undefined
                    ? [
                        { name: 'None', powerlistId: 0 },
                        ...(powerlists ?? []),
                      ].find(
                        (option) =>
                          option.powerlistId ===
                          props.formik.values.nextPowerlistId
                      ) || null
                    : null
                }
                // Handle value change and update formik
                onChange={(_event, value) => {
                  props.formik.setFieldValue(
                    'nextPowerlistId',
                    value?.powerlistId || ''
                  );
                }}
                onClick={() => {
                  trackClickedPowerListNextPowerListDropdown();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
